import * as Sentry from "@sentry/svelte";

/**
 * Browser Sentry config delivered by the backend at runtime via the `sentryConfig` Inertia page
 * prop (ADR-6 — single DSN, no `VITE_SENTRY_DSN`). `null` when Sentry is disabled.
 */
export interface SentryFrontendConfig {
  dsn: string;
  environment?: string | null;
  release?: string | null;
  tracesSampleRate?: number;
}

/**
 * Initialize the browser Sentry SDK. No-ops (returns `false`) when there is no config or no DSN, so
 * an unconfigured environment is a strict no-op.
 *
 * `browserTracingIntegration()` automatically continues the trace from the server-rendered
 * `<meta name="sentry-trace">` / `<meta name="baggage">` tags on pageload (Ch2), and
 * `tracePropagationTargets` attaches the trace headers to same-origin Inertia XHR so the backend
 * continues the trace.
 */
export function initSentry(config: SentryFrontendConfig | null | undefined): boolean {
  if (!config?.dsn) {
    return false;
  }
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment ?? undefined,
    release: config.release ?? undefined,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: config.tracesSampleRate ?? 1.0,
    tracePropagationTargets: [window.location.origin, /^\//],
  });
  return true;
}

/**
 * Read the initial `sentryConfig` page prop from an Inertia mount element's `data-page` attribute.
 * Returns `null` when the element, attribute, JSON, or `sentryConfig` is absent.
 */
export function readInitialSentryConfig(el: HTMLElement | null): SentryFrontendConfig | null {
  const raw = el?.dataset.page;
  if (!raw) {
    return null;
  }
  try {
    const page = JSON.parse(raw) as { props?: { sentryConfig?: SentryFrontendConfig | null } };
    return page.props?.sentryConfig ?? null;
  } catch {
    return null;
  }
}
